<template>
  <div class="notifications-page">
    <section class="notifications-shell">
      <div class="notifications-hero">
        <div class="hero-copy">
          <nav class="notifications-breadcrumb" aria-label="Breadcrumb">
            <span>Home</span>
            <span class="breadcrumb-separator">/</span>
            <span class="breadcrumb-current">Notifications</span>
          </nav>

          <p class="hero-kicker">Inbox</p>
          <h1>Notifications</h1>
          <p class="hero-description">
            Your message center for platform updates, announcements, and follow-ups.
          </p>

          <div class="hero-metrics">
            <article class="metric-chip">
              <span class="metric-chip__label">Alerts</span>
              <strong class="metric-chip__value">{{ filteredNotifications.length }}</strong>
              <span class="metric-chip__meta">Matching results</span>
            </article>
            <article class="metric-chip">
              <span class="metric-chip__label">Unread</span>
              <strong class="metric-chip__value">{{ unreadCount }}</strong>
              <span class="metric-chip__meta">Pending review</span>
            </article>
          </div>
        </div>
      </div>

      <section class="toolbar">
        <div class="toolbar-copy">
          <p class="results-kicker">Message Center</p>
          <h2>Admin notifications</h2>
        </div>

        <div class="toolbar-actions">
          <button type="button" class="create-button" @click="openCreateModal">
            <i class="fas fa-plus" aria-hidden="true"></i>
            <span>Create notification</span>
          </button>
          <label class="search-shell" for="notification-search">
            <i class="fas fa-search" aria-hidden="true"></i>
            <input
              id="notification-search"
              v-model.trim="search"
              type="search"
              placeholder="Search titles or messages"
            />
          </label>
        </div>
      </section>

      <div
        v-if="showCreateModal"
        class="notification-modal-backdrop"
        @click.self="closeCreateModal"
      >
        <div class="notification-modal">
          <div class="notification-modal__hero">
            <div>
              <span class="notification-modal__eyebrow">Broadcast</span>
              <h3>
                {{
                  isGlobalDraft ? 'Create a public notification' : 'Create a direct notification'
                }}
              </h3>
              <p>
                {{
                  isGlobalDraft
                    ? 'Send a platform-wide update that every user can see in their notifications feed.'
                    : 'Choose an organization to notify all of its attached email addresses.'
                }}
              </p>
            </div>
            <button
              type="button"
              class="notification-modal__close"
              aria-label="Close modal"
              @click="closeCreateModal"
            >
              <i class="fas fa-times" aria-hidden="true"></i>
            </button>
          </div>

          <form class="notification-form" @submit.prevent="submitNotification">
            <label class="notification-field" for="notification-delivery">
              <span>Delivery</span>
              <select id="notification-delivery" v-model="createForm.deliveryType">
                <option value="public">Public notification</option>
                <option value="direct">Direct notification</option>
              </select>
            </label>

            <div v-if="!isGlobalDraft" class="organization-picker">
              <div class="organization-picker__heading">
                <label for="notification-recipient">Organization</label>
                <span>Choose one recipient</span>
              </div>
              <multiselect
                id="notification-recipient"
                v-model="selectedOrganization"
                class="organization-select"
                :options="recipientOptions"
                label="label"
                track-by="id"
                placeholder="Search organizations by name…"
                :loading="recipientsLoading"
                :disabled="recipientsLoading"
                :max-height="240"
                :show-labels="false"
                :allow-empty="false"
                :option-height="56"
                open-direction="below"
              >
                <template slot="option" slot-scope="{ option }">
                  <span class="organization-option">
                    <span class="organization-option__icon" aria-hidden="true">
                      <i class="far fa-building"></i>
                    </span>
                    <span class="organization-option__name">{{ option.label }}</span>
                    <i
                      v-if="option.id === createForm.recipient"
                      class="fas fa-check"
                      aria-hidden="true"
                    ></i>
                  </span>
                </template>
                <template slot="noResult">No organizations match your search.</template>
                <template slot="noOptions">No organizations are available.</template>
              </multiselect>
              <p v-if="!createForm.recipient" class="organization-picker__hint">
                Select an organization to preview its delivery addresses.
              </p>
              <div v-else class="organization-delivery" aria-live="polite">
                <div class="organization-delivery__heading">
                  <span><i class="far fa-envelope" aria-hidden="true"></i> Delivery addresses</span>
                  <span class="organization-delivery__count"
                    >{{ selectedOrganizationEmails.length }}
                    {{ selectedOrganizationEmails.length === 1 ? 'email' : 'emails' }}</span
                  >
                </div>
                <ul v-if="selectedOrganizationEmails.length" class="organization-delivery__emails">
                  <li v-for="email in selectedOrganizationEmails" :key="email">{{ email }}</li>
                </ul>
                <p v-else class="organization-picker__hint">
                  No email addresses are attached to this organization.
                </p>
              </div>
            </div>

            <label class="notification-field" for="notification-title">
              <span>Title</span>
              <input
                id="notification-title"
                v-model.trim="createForm.title"
                type="text"
                maxlength="120"
                required
                placeholder="Enter a short notification title"
              />
            </label>

            <label class="notification-field" for="notification-message">
              <span>Message</span>
              <textarea
                id="notification-message"
                v-model.trim="createForm.message"
                rows="5"
                maxlength="500"
                required
                placeholder="Write the public update you want users to receive"
              ></textarea>
            </label>

            <div class="notification-modal__meta">
              <div class="notification-meta-pill">
                <span>Audience</span>
                <strong>{{ isGlobalDraft ? 'Public' : 'Organization' }}</strong>
              </div>
              <div class="notification-meta-pill">
                <span>Delivery</span>
                <strong>{{ isGlobalDraft ? 'Global' : 'Targeted' }}</strong>
              </div>
            </div>

            <div class="notification-modal__actions">
              <button
                type="button"
                class="modal-button modal-button--ghost"
                @click="closeCreateModal"
              >
                Cancel
              </button>
              <button type="submit" class="modal-button modal-button--primary" :disabled="creating">
                <b-spinner v-if="creating" small />
                <span>{{ creating ? 'Publishing...' : 'Publish notification' }}</span>
              </button>
            </div>
          </form>
        </div>
      </div>

      <div v-if="loading" class="loading-state">
        <b-spinner />
        <p>Loading notifications...</p>
      </div>

      <template v-else>
        <div
          v-if="paginatedNotifications.length"
          class="notification-table-scroll"
          role="region"
          aria-label="Notifications table"
          tabindex="0"
        >
          <table id="notification-list" class="notification-table">
            <caption class="sr-only">
              Platform notifications, audiences, read status, and available actions
            </caption>
            <thead>
              <tr>
                <th scope="col" class="number-column">No.</th>
                <th scope="col" class="message-column">Notification</th>
                <th scope="col">Audience</th>
                <th scope="col">Status</th>
                <th scope="col" class="actions-column">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, index) in paginatedNotifications"
                :key="item.id || index"
                :class="{ 'notification-row--unread': item.is_read === false }"
              >
                <td class="row-number">{{ padIndex((currentPage - 1) * perPage + index) }}</td>
                <th scope="row" class="notification-content">
                  <div class="notification-summary">
                    <span
                      class="notification-symbol"
                      :class="{ 'notification-symbol--global': item.is_global }"
                      aria-hidden="true"
                    >
                      <i :class="item.is_global ? 'fas fa-bullhorn' : 'far fa-envelope'"></i>
                    </span>
                    <div class="notification-copy">
                      <span class="notification-title">{{ item.title || 'Notification' }}</span>
                      <p class="notification-message">
                        {{ item.message || 'No notification message was provided.' }}
                      </p>
                    </div>
                  </div>
                </th>
                <td>
                  <span class="audience-label">{{
                    item.is_global ? 'All users' : recipientLabel(item.recipient)
                  }}</span>
                  <span class="delivery-label">{{
                    item.is_global ? 'Global broadcast' : 'Direct message'
                  }}</span>
                </td>
                <td>
                  <span
                    class="read-status"
                    :class="item.is_read === false ? 'read-status--unread' : 'read-status--read'"
                  >
                    <span class="status-dot" aria-hidden="true"></span>
                    {{ item.is_read === false ? 'Unread' : 'Read' }}
                  </span>
                </td>
                <td class="actions-column">
                  <button
                    v-if="item.is_read === false && item.id"
                    type="button"
                    class="action-button"
                    :aria-label="`Mark ${item.title || 'notification'} as read`"
                    @click="markNotificationAsRead(item.id)"
                  >
                    <i class="fas fa-check" aria-hidden="true"></i>
                    Mark as read
                  </button>
                  <span v-else-if="item.is_read !== false" class="reviewed-label"
                    ><i class="fas fa-check-double" aria-hidden="true"></i> Reviewed</span
                  >
                  <span v-else class="reviewed-label">Unavailable</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else class="empty-state">
          <h3>No notifications found</h3>
          <p>There are no notifications matching the current search right now.</p>
        </div>

        <div v-if="filteredNotifications.length" class="pagination-shell">
          <div class="pagination-copy">
            <label class="page-size-shell" for="notification-page-size">
              <span>Rows</span>
              <select id="notification-page-size" v-model="perPage">
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
              </select>
            </label>
            <p>Page {{ currentPage }} of {{ totalPages }}</p>
            <span
              >{{ filteredNotifications.length }} notification{{
                filteredNotifications.length === 1 ? '' : 's'
              }}</span
            >
          </div>

          <b-pagination
            v-model="currentPage"
            :total-rows="filteredNotifications.length"
            :per-page="perPage"
            pills
            align="right"
            aria-controls="notification-list"
          />
        </div>
      </template>
    </section>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  createNotification,
  getNotifications,
  getOrganizationEmails,
  markNotificationAsRead,
} from '../config/api-modules/api.notifications';

export default {
  name: 'AdminNotificationsPage',

  data() {
    return {
      loading: true,
      creating: false,
      recipientsLoading: false,
      showCreateModal: false,
      search: '',
      currentPage: 1,
      perPage: 10,
      notifications: [],
      recipientOptions: [],
      createForm: {
        deliveryType: 'public',
        recipient: '',
        title: '',
        message: '',
      },
    };
  },

  computed: {
    ...mapGetters('auth', ['user']),

    isAdminViewer() {
      return [1, 2, 6].includes(Number(this.user?.type));
    },

    unreadCount() {
      return this.notifications.filter((item) => item.is_read === false).length;
    },

    filteredNotifications() {
      const searchTerm = this.search.toLowerCase();

      return this.notifications.filter((item) => {
        const haystack = [item.title, item.message].filter(Boolean).join(' ').toLowerCase();

        return haystack.includes(searchTerm);
      });
    },

    paginatedNotifications() {
      return this.filteredNotifications.slice(
        (this.currentPage - 1) * this.perPage,
        this.currentPage * this.perPage
      );
    },

    totalPages() {
      return Math.max(1, Math.ceil(this.filteredNotifications.length / this.perPage));
    },

    selectedOrganization: {
      get() {
        return this.recipientOptions.find((item) => item.id === this.createForm.recipient) || null;
      },
      set(organization) {
        this.createForm.recipient = organization?.id || '';
      },
    },

    selectedOrganizationEmails() {
      return (
        this.recipientOptions.find((item) => item.id === this.createForm.recipient)?.emails || []
      );
    },

    isGlobalDraft() {
      return this.createForm.deliveryType === 'public';
    },
  },

  watch: {
    search() {
      this.currentPage = 1;
    },

    perPage() {
      this.currentPage = 1;
    },

    'createForm.deliveryType': function onDeliveryTypeChange(value) {
      if (value === 'public') {
        this.createForm.recipient = '';
      }
    },
  },

  methods: {
    async openCreateModal() {
      this.showCreateModal = true;
      if (!this.recipientOptions.length) {
        await this.fetchRecipients();
      }
    },

    closeCreateModal(force = false) {
      if (this.creating && !force) {
        return;
      }

      this.showCreateModal = false;
      this.createForm = {
        deliveryType: 'public',
        recipient: '',
        title: '',
        message: '',
      };
    },

    padIndex(index) {
      return String(index + 1).padStart(2, '0');
    },

    recipientLabel(recipient) {
      if (recipient === this.user?.id) {
        return 'You';
      }

      if (recipient === null || recipient === undefined) {
        return 'Unassigned';
      }

      return `User #${recipient}`;
    },

    normalizeResponse(response) {
      const records = response?.data?.results || response?.data || [];
      return Array.isArray(records) ? records : [];
    },

    normalizeRecipients(response) {
      const records = response?.data?.results || response?.data || [];
      if (!Array.isArray(records)) {
        return [];
      }

      return records.map((item) => {
        const name = item?.name || 'Unnamed organization';
        const emails = Array.isArray(item?.emails) ? item.emails.filter(Boolean) : [];

        return {
          id: item?.id,
          label: name,
          emails: [...new Set(emails)],
        };
      });
    },

    async fetchRecipients() {
      this.recipientsLoading = true;

      try {
        const response = await getOrganizationEmails();
        this.recipientOptions = this.normalizeRecipients(response).filter((item) => item.id);
      } catch (error) {
        this.recipientOptions = [];
        this.$toast.show({
          type: 'ERROR',
          message: 'Unable to load organizations for direct notifications',
        });
      } finally {
        this.recipientsLoading = false;
      }
    },

    async fetchNotifications() {
      this.loading = true;

      try {
        const response = await getNotifications(this.isAdminViewer ? null : this.user?.id);
        this.notifications = this.normalizeResponse(response);
      } catch (error) {
        this.notifications = [];
        this.$toast.show({
          type: 'ERROR',
          message: 'Unable to load notifications',
        });
      } finally {
        this.loading = false;
      }
    },

    async submitNotification() {
      if (this.createForm.deliveryType === 'direct' && !this.createForm.recipient) {
        this.$toast.show({
          type: 'ERROR',
          message: 'Please select an organization for the direct notification',
        });
        return;
      }

      this.creating = true;

      const payload = {
        title: this.createForm.title,
        message: this.createForm.message,
        is_global: this.createForm.deliveryType === 'public',
        ...(this.createForm.deliveryType === 'direct'
          ? { organization: this.createForm.recipient }
          : {}),
      };

      try {
        const response = await createNotification(payload);
        const createdNotification = response?.data || payload;

        this.notifications = [createdNotification, ...this.notifications];
        this.closeCreateModal(true);
        this.$toast.show({
          type: 'SUCCESS',
          message: payload.is_global
            ? 'Public notification created'
            : 'Direct notification created',
        });
      } catch (error) {
        this.$toast.show({
          type: 'ERROR',
          message: 'Unable to create notification',
        });
      } finally {
        this.creating = false;
      }
    },

    async markNotificationAsRead(id) {
      try {
        await markNotificationAsRead(id);
        this.notifications = this.notifications.map((item) =>
          item.id === id ? { ...item, is_read: true } : item
        );
        this.$toast.show({
          type: 'SUCCESS',
          message: 'Notification marked as read',
        });
      } catch (error) {
        this.$toast.show({
          type: 'ERROR',
          message: 'Unable to mark notification as read',
        });
      }
    },
  },

  mounted() {
    this.fetchNotifications();
  },
};
</script>

<style lang="scss" scoped>
.notifications-page {
  min-height: 100vh;
  padding: 1.5rem 1rem 2rem;
  background: radial-gradient(ellipse at top right, #e9f0f6, transparent 55%), #f5f7fa;
}

.notifications-shell {
  max-width: 1320px;
  margin: 0 auto;
}

.notifications-hero {
  display: block;
  padding: 1.75rem;
  border: 1px solid rgba(20, 70, 110, 0.08);
  border-radius: 28px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.97), rgba(243, 249, 255, 0.92)), #fff;
  box-shadow: 0 8px 32px rgba(29, 61, 94, 0.04);
}

.notifications-breadcrumb {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
  color: #6f7d8f;
  font-size: 0.9rem;
}

.breadcrumb-separator {
  opacity: 0.55;
}

.breadcrumb-current {
  color: #204d74;
  font-weight: 600;
}

.hero-kicker,
.results-kicker {
  margin-bottom: 0.65rem;
  color: #1f6da8;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.hero-copy h1,
.toolbar-copy h2 {
  margin: 0;
  color: #153754;
  line-height: 1.08;
}

.hero-copy h1 {
  font-size: clamp(1.9rem, 3vw, 2.6rem);
  letter-spacing: -0.04em;
  font-weight: 700;
  width: 100%;
  max-width: none;
}

.hero-description {
  max-width: 60ch;
  margin: 1rem 0 0;
  color: #5f7488;
  font-size: 1rem;
  line-height: 1.7;
}

.hero-metrics {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  margin-top: 1.35rem;
}

.metric-chip {
  display: grid;
  grid-template-columns: auto auto;
  align-items: center;
  gap: 0.1rem 0.9rem;
  min-width: 180px;
  padding: 0.9rem 1rem;
  border-radius: 20px;
  background: rgba(244, 249, 255, 0.9);
  border: 1px solid rgba(20, 70, 110, 0.1);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
}

.metric-chip__label {
  grid-column: 1;
  grid-row: 1;
  color: #6a8196;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.metric-chip__value {
  grid-column: 2;
  grid-row: 1 / span 2;
  justify-self: end;
  color: #153754;
  font-size: 2rem;
  line-height: 1;
}

.metric-chip__meta {
  grid-column: 1;
  grid-row: 2;
  color: #62798e;
  font-size: 0.85rem;
  line-height: 1.35;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.5rem;
  padding: 1.2rem 1.3rem;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(20, 70, 110, 0.08);
  box-shadow: 0 16px 35px rgba(29, 61, 94, 0.06);
}

.toolbar-copy h2 {
  font-size: clamp(1.3rem, 2.5vw, 2rem);
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.create-button {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  border: none;
  border-radius: 999px;
  padding: 0.85rem 1rem;
  background: linear-gradient(135deg, #1f6da8, #3d95cf);
  color: #ffffff;
  font-weight: 700;
  box-shadow: 0 12px 26px rgba(31, 109, 168, 0.18);
  width: fit-content;
}

.notification-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: grid;
  place-items: center;
  padding: 1rem;
  background: rgba(11, 28, 43, 0.34);
  backdrop-filter: blur(6px);
}

.notification-modal {
  width: min(100%, 640px);
  max-height: calc(100vh - 2rem);
  overflow-y: auto;
  border-radius: 28px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(245, 250, 255, 0.96));
  border: 1px solid rgba(20, 70, 110, 0.08);
  box-shadow: 0 24px 60px rgba(18, 45, 69, 0.22);
}

.notification-modal__hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.4rem 1.4rem 1rem;
  border-bottom: 1px solid rgba(20, 70, 110, 0.08);
}

.notification-modal__eyebrow {
  display: inline-block;
  margin-bottom: 0.55rem;
  color: #1f6da8;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.notification-modal__hero h3 {
  margin: 0;
  color: #153754;
  font-size: 1.6rem;
}

.notification-modal__hero p {
  margin: 0.7rem 0 0;
  color: #5f7488;
  line-height: 1.6;
}

.notification-modal__close {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 999px;
  background: rgba(20, 70, 110, 0.08);
  color: #37556f;
}

.notification-form {
  display: grid;
  gap: 1rem;
  padding: 1.35rem 1.4rem 1.4rem;
}

.notification-field {
  display: grid;
  gap: 0.5rem;
}

.notification-field span {
  color: #33536e;
  font-size: 0.9rem;
  font-weight: 700;
}

.notification-field input,
.notification-field select,
.notification-field textarea {
  width: 100%;
  border: 1px solid rgba(20, 70, 110, 0.14);
  border-radius: 18px;
  padding: 0.95rem 1rem;
  background: #f8fbff;
  color: #153754;
  outline: none;
}

.notification-field textarea {
  resize: vertical;
  min-height: 140px;
}

.organization-picker {
  padding: 1.1rem;
  border: 1px solid #dfe8ee;
  border-radius: 16px;
  background: #fff;
}
.organization-picker__heading,
.organization-delivery__heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}
.organization-picker__heading label {
  margin: 0;
  color: #253f53;
  font-size: 0.88rem;
  font-weight: 700;
}
.organization-picker__heading > span,
.organization-picker__hint {
  color: #7b8b99;
  font-size: 0.75rem;
}
.organization-picker__hint {
  margin: 0.7rem 0 0;
  line-height: 1.6;
}
.organization-select ::v-deep .multiselect__tags {
  min-height: 50px;
  padding: 14px 36px 8px 14px;
  border: 1px solid #dce5ec;
  border-radius: 10px;
  background: #f8fafc;
}
.organization-select ::v-deep .multiselect__select {
  height: 48px;
}
.organization-select ::v-deep .multiselect__input,
.organization-select ::v-deep .multiselect__single {
  padding: 0;
  background: transparent;
  color: #253f53;
  font-size: 0.85rem;
  line-height: 1.5;
}
.organization-select ::v-deep .multiselect__placeholder {
  padding: 0;
  margin-bottom: 4px;
  color: #80909e;
  font-size: 0.83rem;
}
.organization-select.multiselect--active ::v-deep .multiselect__tags {
  border-color: #76a4c1;
  box-shadow: 0 0 0 3px #eaf2f7;
}
.organization-select ::v-deep .multiselect__content-wrapper {
  margin-top: 6px;
  padding: 5px;
  border: 1px solid #dfe7ee;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 14px 32px #193d5724;
}
.organization-select ::v-deep .multiselect__option {
  min-height: 56px;
  padding: 10px;
  border-radius: 8px;
  white-space: normal;
  color: #425b70;
  font-size: 0.83rem;
}
.organization-select ::v-deep .multiselect__option--highlight {
  background: #edf4f9;
  color: #1c5c88;
}
.organization-select ::v-deep .multiselect__option--selected {
  background: #e7f0f7;
  color: #205c83;
  font-weight: 600;
}
.organization-option {
  display: flex;
  align-items: center;
  gap: 10px;
  line-height: 1.5;
}
.organization-option__icon {
  display: grid;
  place-items: center;
  flex: 0 0 32px;
  height: 32px;
  border: 1px solid #e2eaf0;
  border-radius: 8px;
  background: #fff;
  color: #7790a4;
}
.organization-option__name {
  flex: 1;
  overflow-wrap: anywhere;
}
.organization-delivery {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #edf1f4;
}
.organization-delivery__heading {
  color: #617889;
  font-size: 0.73rem;
  font-weight: 600;
}
.organization-delivery__heading i {
  margin-right: 5px;
}
.organization-delivery__count {
  color: #7c909f;
  font-weight: 400;
}
.organization-delivery__emails {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.organization-delivery__emails li {
  padding: 6px 10px;
  border: 1px solid #e0eaf0;
  border-radius: 7px;
  background: #f4f8fb;
  color: #3d617b;
  font-size: 0.76rem;
  overflow-wrap: anywhere;
  max-width: 100%;
}

.notification-modal__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.notification-meta-pill {
  min-width: 140px;
  padding: 0.85rem 0.95rem;
  border-radius: 18px;
  background: rgba(244, 249, 255, 0.95);
  border: 1px solid rgba(20, 70, 110, 0.08);
}

.notification-meta-pill span {
  display: block;
  color: #6a8196;
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.notification-meta-pill strong {
  display: block;
  margin-top: 0.3rem;
  color: #153754;
}

.notification-modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.modal-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  border: none;
  border-radius: 999px;
  padding: 0.82rem 1.15rem;
  font-weight: 700;
  width: fit-content;
}

.modal-button--ghost {
  background: rgba(20, 70, 110, 0.08);
  color: #35546d;
}

.modal-button--primary {
  background: linear-gradient(135deg, #1f6da8, #3d95cf);
  color: #ffffff;
}

.search-shell {
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  min-width: min(360px, 100%);
  padding: 0.85rem 1rem;
  margin: 0;
  border-radius: 10px;
  background: #f7fbff;
  border: 1px solid rgba(20, 70, 110, 0.1);
  color: #63819d;
}

.search-shell input {
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  color: #153754;
}

.loading-state,
.empty-state {
  display: grid;
  place-items: center;
  gap: 0.85rem;
  min-height: 320px;
  margin-top: 1.5rem;
  padding: 2rem;
  text-align: center;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.86);
  border: 1px solid rgba(20, 70, 110, 0.08);
  color: #587086;
  box-shadow: 0 18px 40px rgba(29, 61, 94, 0.06);
}

.empty-state h3 {
  margin: 0;
  color: #163a57;
}

.empty-state p,
.loading-state p {
  margin: 0;
}

.notification-table-scroll {
  overflow-x: auto;
  margin-top: 1.25rem;
  border: 1px solid #e2e9ef;
  border-radius: 18px 18px 0 0;
  background: #fff;
  box-shadow: 0 12px 36px rgba(24, 52, 77, 0.04);
}

.notification-table {
  width: 100%;
  min-width: 820px;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
  text-align: left;
}

.notification-table thead th {
  padding: 1.1rem 1.25rem;
  background: #f6f8fb;
  border-bottom: 1px solid #e2e9ef;
  color: #6d7f90;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.notification-table tbody td,
.notification-table tbody th {
  padding: 1.5rem 1.25rem;
  border-bottom: 1px solid #edf1f5;
  vertical-align: middle;
  transition: background 0.15s ease;
}

.notification-table tbody tr:last-child > * {
  border-bottom: 0;
}
.notification-table tbody tr:hover > * {
  background: #f5f9fc;
}
.notification-table .number-column {
  width: 65px;
}
.notification-table .message-column {
  width: 44%;
}
.notification-table .actions-column {
  width: 170px;
  text-align: right;
}
.notification-table .row-number {
  color: #8b9ba9;
  font-size: 0.75rem;
  font-variant-numeric: tabular-nums;
}
.notification-row--unread {
  background: #fafdff;
}
.notification-row--unread > :first-child {
  box-shadow: inset 3px 0 #3788b9;
}
.notification-summary {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}
.notification-symbol {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border: 1px solid #e5e7f3;
  border-radius: 12px;
  color: #7573a7;
  background: #f3f2fa;
}
.notification-symbol--global {
  border-color: #dfecf6;
  color: #4383ae;
  background: #edf5fc;
}
.notification-copy {
  min-width: 0;
}
.notification-title {
  display: block;
  color: #243e55;
  font-size: 0.91rem;
  font-weight: 600;
  line-height: 1.5;
  overflow-wrap: anywhere;
}
.notification-row--unread .notification-title {
  color: #143c5d;
  font-weight: 700;
}
.notification-message {
  margin: 0.4rem 0 0;
  color: #718396;
  font-size: 0.81rem;
  font-weight: 400;
  line-height: 1.7;
  overflow-wrap: anywhere;
  white-space: pre-line;
}
.audience-label {
  display: block;
  color: #415b70;
  font-size: 0.82rem;
  font-weight: 600;
  overflow-wrap: anywhere;
}
.delivery-label {
  display: block;
  margin-top: 0.3rem;
  color: #8594a1;
  font-size: 0.7rem;
}
.read-status {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.35rem 0.65rem;
  border-radius: 6px;
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
}
.read-status--unread {
  color: #276e9d;
  background: #e8f2fa;
}
.read-status--read {
  color: #547b63;
  background: #eef5f0;
}
.status-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: currentColor;
}
.action-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid #d9e5ed;
  border-radius: 8px;
  background: #fff;
  color: #286b99;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.action-button:hover {
  background: #eaf4fb;
  border-color: #8bb7d6;
}
.reviewed-label {
  display: inline-flex;
  gap: 0.5rem;
  align-items: center;
  color: #87978f;
  font-size: 0.75rem;
}
.notification-table-scroll:focus-visible,
.action-button:focus-visible,
.create-button:focus-visible {
  outline: 3px solid #71aacf;
  outline-offset: 3px;
}
.search-shell:focus-within {
  border-color: #71aacf;
  box-shadow: 0 0 0 3px #dcecf7;
}

.pagination-shell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 0;
  padding: 1rem 1.2rem;
  border-radius: 0 0 18px 18px;
  background: #fff;
  border: 1px solid rgba(20, 70, 110, 0.08);
  box-shadow: 0 16px 36px rgba(29, 61, 94, 0.06);
}

.pagination-copy {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.9rem;
  color: #5d7389;
}

.pagination-copy p,
.pagination-copy span {
  margin: 0;
}

.page-size-shell {
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
}

.page-size-shell select {
  border-radius: 999px;
  padding: 0.45rem 0.8rem;
  border: 1px solid rgba(20, 70, 110, 0.12);
  background: #f7fbff;
  color: #153754;
}

.notifications-page ::v-deep .pagination {
  margin-bottom: 0;
}

.notifications-page ::v-deep .page-item .page-link {
  border-radius: 999px;
  margin-left: 0.35rem;
  border: none;
  color: #1f6da8;
  background: rgba(31, 109, 168, 0.08);
}

.notifications-page ::v-deep .page-item.active .page-link {
  color: #ffffff;
  background: linear-gradient(135deg, #1f6da8, #3d95cf);
  box-shadow: 0 10px 20px rgba(31, 109, 168, 0.18);
}

@media (max-width: 991px) {
  .notifications-page {
    padding: 0;
  }

  .notifications-shell {
    margin-top: 0;
  }

  .toolbar,
  .pagination-shell {
    grid-template-columns: 1fr;
    flex-direction: column;
    align-items: stretch;
  }

  .notification-modal__hero,
  .notification-modal__actions {
    flex-direction: column;
  }

  .metric-chip {
    flex: 1;
    min-width: 140px;
  }

  .search-shell {
    min-width: 0;
    width: 100%;
  }
}

@media (max-width: 767px) {
  .notifications-shell {
    margin-top: 0;
  }
}

@media (max-width: 600px) {
  .notifications-shell {
    margin-top: 0;
  }
}
</style>
